import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  useCallback,
  useMemo,
  type ReactNode,
} from "react"
import {
  useAgentBuilderClient,
  useAgentBuilderConfig,
} from "./AgentBuilderProvider"
import {
  AgentBuilderClient,
  ThreadConnectionManager,
  FileUploadManager,
  transformToWorkblocks,
  transformToSubagentBlocks,
  type Message,
  type GroupedThreadMessage,
  type SubagentThreadMessage,
  type ThreadMessage,
  type ThreadFile,
  type SendMessagePayload,
  type PendingAttachment,
  type AttachmentPayload,
  type ConnectionStatus as ClientConnectionStatus,
} from "@standardagents/client"
import type { ThreadProviderOptions } from "../types"
import { createClientId } from "../utils/id"

// Shared file upload manager instance
const uploadManager = new FileUploadManager()

/**
 * Event listener callback type
 */
type EventListener<T = unknown> = (data: T) => void

/**
 * WebSocket connection status
 */
export type ConnectionStatus = ClientConnectionStatus

// =============================================================================
// INTERNAL CONTEXTS (not exported - implementation detail)
// =============================================================================

/**
 * Static context - things that don't change or are memoized callbacks
 */
interface StaticContextValue {
  threadId: string
  options: ThreadProviderOptions
  sendMessage: (payload: Omit<SendMessagePayload, 'attachments'>) => Promise<Message>
  stopExecution: () => Promise<void>
  deleteMessage: (messageId: string) => Promise<void>
  subscribeToEvent: <T = unknown>(
    eventType: string,
    listener: EventListener<T>
  ) => () => void
  // File management (uploads to filesystem)
  addFiles: (files: File[] | FileList) => void
  removeFile: (id: string) => void
  getFileUrl: (file: ThreadFile) => string
  getThumbnailUrl: (file: ThreadFile) => string
  getPreviewUrl: (file: ThreadFile) => string | null
  // Attachment management (sent inline with messages)
  addAttachment: (files: File | File[] | FileList) => void
  removeAttachment: (id: string) => void
  clearAttachments: () => void
}

/**
 * Messages context - changes when messages update
 */
interface MessagesContextValue {
  messages: Message[]
  workblocks: ThreadMessage[]
  subagentBlocks: SubagentThreadMessage[]
  groupedMessages: GroupedThreadMessage[]
  loading: boolean
}

/**
 * Files context - changes when files update
 */
interface FilesContextValue {
  files: ThreadFile[]
}

/**
 * Attachments context - pending attachments to send with next message
 */
interface AttachmentsContextValue {
  attachments: PendingAttachment[]
}

/**
 * Connection context - changes when connection status changes
 */
interface ConnectionContextValue {
  connectionStatus: ConnectionStatus
  error: Error | null
}

const StaticContext = createContext<StaticContextValue | null>(null)
const MessagesContext = createContext<MessagesContextValue | null>(null)
const FilesContext = createContext<FilesContextValue | null>(null)
const AttachmentsContext = createContext<AttachmentsContextValue | null>(null)
const ConnectionContext = createContext<ConnectionContextValue | null>(null)

// =============================================================================
// PUBLIC TYPES AND CONTEXT
// =============================================================================

/**
 * Thread context value - the public interface returned by useThread()
 */
export interface ThreadContextValue {
  /** The thread ID */
  threadId: string
  /** Current messages in the thread */
  messages: Message[]
  /** Messages transformed to workblocks (if useWorkblocks is true) */
  workblocks: ThreadMessage[]
  /** Messages transformed to subagent blocks (if useSubagentBlocks is true) */
  subagentBlocks: SubagentThreadMessage[]
  /** Active grouped message view based on options (subagent blocks > workblocks > raw) */
  groupedMessages: GroupedThreadMessage[]
  /** Whether messages are currently loading (alias: isLoading) */
  loading: boolean
  /** Whether messages are currently loading (alias for loading) */
  isLoading: boolean
  /** Any error that occurred */
  error: Error | null
  /** WebSocket connection status (alias: status) */
  connectionStatus: ConnectionStatus
  /** WebSocket connection status (alias for connectionStatus) */
  status: ConnectionStatus
  /** Subscribe to a specific event type (alias: onEvent) */
  subscribeToEvent: <T = unknown>(
    eventType: string,
    listener: EventListener<T>
  ) => () => void
  /** Subscribe to a specific event type (alias for subscribeToEvent) */
  onEvent: <T = unknown>(
    eventType: string,
    listener: EventListener<T>
  ) => () => void
  /** Options passed to the provider */
  options: ThreadProviderOptions

  // Actions
  /** Send a message to the thread (auto-includes pending attachments) */
  sendMessage: (payload: Omit<SendMessagePayload, 'attachments'>) => Promise<Message>
  /** Stop the current execution */
  stopExecution: () => Promise<void>
  /** Delete a message from the thread (optimistically removes from UI) */
  deleteMessage: (messageId: string) => Promise<void>

  // File management (uploads to filesystem, NOT sent to LLM)
  /** All files in the thread (pending uploads + committed from messages) */
  files: ThreadFile[]
  /** Add files and start uploading immediately to filesystem */
  addFiles: (files: File[] | FileList) => void
  /** Remove a pending file (cannot remove committed files) */
  removeFile: (id: string) => void
  /** Get the full URL for a file */
  getFileUrl: (file: ThreadFile) => string
  /** Get the thumbnail URL for an image file */
  getThumbnailUrl: (file: ThreadFile) => string
  /** Get preview URL - localPreviewUrl for pending images, thumbnail for committed */
  getPreviewUrl: (file: ThreadFile) => string | null

  // Attachment management (sent inline with messages to LLM)
  /** Pending attachments to be sent with next message */
  attachments: PendingAttachment[]
  /** Add attachment(s) to be sent with next message (no upload, stored locally) */
  addAttachment: (files: File | File[] | FileList) => void
  /** Remove a pending attachment */
  removeAttachment: (id: string) => void
  /** Clear all pending attachments */
  clearAttachments: () => void
}

/**
 * Legacy context export for useThread() - combines all internal contexts
 * @internal
 */
export const ThreadContext = createContext<ThreadContextValue | null>(null)

export interface ThreadProviderProps {
  /** The thread ID to connect to */
  threadId: string
  /** Provider options */
  options?: ThreadProviderOptions
  /** Whether to preload messages on mount (default: true) */
  preload?: boolean
  /** Whether to enable live updates via WebSocket (default: true) */
  live?: boolean
  /** Transform messages to workblocks (default: false) */
  useWorkblocks?: boolean
  /** Transform messages to subagent blocks (default: false) */
  useSubagentBlocks?: boolean
  /** Maximum message depth to fetch/stream (default: 0 for top-level only) */
  depth?: number
  /** Whether to include silent messages (default: false) */
  includeSilent?: boolean
  /** Optional endpoint override */
  endpoint?: string
  children: ReactNode
}

/**
 * ThreadProvider establishes a WebSocket connection to a thread and provides
 * context for child components to access messages and events.
 *
 * Must be nested inside AgentBuilderProvider.
 *
 * @example
 * ```tsx
 * <AgentBuilderProvider config={{ endpoint: 'https://api.example.com' }}>
 *   <ThreadProvider threadId="thread-123">
 *     <ChatMessages />
 *   </ThreadProvider>
 * </AgentBuilderProvider>
 * ```
 */
export function ThreadProvider({
  threadId,
  options = {},
  preload = true,
  live = true,
  useWorkblocks = false,
  useSubagentBlocks = false,
  depth = 0,
  includeSilent = false,
  endpoint: endpointOverride,
  children,
}: ThreadProviderProps) {
  const contextClient = useAgentBuilderClient()
  const contextConfig = useAgentBuilderConfig()

  // Use override endpoint if provided, otherwise use context endpoint
  const effectiveEndpoint = endpointOverride || contextConfig.endpoint

  // Create a client instance
  const clientRef = useRef<AgentBuilderClient>(
    endpointOverride ? new AgentBuilderClient(endpointOverride) : contextClient
  )

  // Update client if endpoint override changes
  useEffect(() => {
    if (endpointOverride) {
      clientRef.current = new AgentBuilderClient(endpointOverride)
    } else {
      clientRef.current = contextClient
    }
  }, [endpointOverride, contextClient])

  // =============================================================================
  // STATE
  // =============================================================================

  const [messages, setMessages] = useState<Message[]>([])
  const [loading, setLoading] = useState(preload)
  const [error, setError] = useState<Error | null>(null)
  const [connectionStatus, setConnectionStatus] = useState<ConnectionStatus>(
    live ? "connecting" : "disconnected"
  )
  const [pendingFiles, setPendingFiles] = useState<ThreadFile[]>([])
  const [serverFiles, setServerFiles] = useState<ThreadFile[]>([])
  const [attachments, setAttachments] = useState<PendingAttachment[]>([])

  // Keep a ref to current messages for optimistic update rollback
  const messagesRef = useRef<Message[]>(messages)
  messagesRef.current = messages

  // =============================================================================
  // DERIVED STATE
  // =============================================================================

  const files = useMemo(() => {
    // Deduplicate: serverFiles take priority over pending files with same path
    const serverPaths = new Set(serverFiles.map((f) => f.path))
    const uniquePendingFiles = pendingFiles.filter(
      (f) => !f.path || !serverPaths.has(f.path)
    )
    return [...uniquePendingFiles, ...serverFiles]
  }, [pendingFiles, serverFiles])

  const workblocks = useMemo(() => {
    if (!useWorkblocks) {
      return messages as ThreadMessage[]
    }
    return transformToWorkblocks(messages)
  }, [messages, useWorkblocks])

  const subagentBlocks = useMemo(() => {
    if (!useSubagentBlocks) {
      return messages as SubagentThreadMessage[]
    }
    return transformToSubagentBlocks(messages, {
      includeWorkblocks: useWorkblocks,
    })
  }, [messages, useSubagentBlocks, useWorkblocks])

  const groupedMessages = useMemo(() => {
    if (useSubagentBlocks) {
      return subagentBlocks as GroupedThreadMessage[]
    }
    if (useWorkblocks) {
      return workblocks as GroupedThreadMessage[]
    }
    return messages as GroupedThreadMessage[]
  }, [messages, workblocks, subagentBlocks, useSubagentBlocks, useWorkblocks])

  // =============================================================================
  // REFS
  // =============================================================================

  const eventListenersRef = useRef<Map<string, Set<EventListener>>>(new Map())
  const connectionManagerRef = useRef<ThreadConnectionManager | null>(null)

  // =============================================================================
  // HELPERS
  // =============================================================================

  /**
   * Convert a File to base64 string
   */
  const fileToBase64 = useCallback((file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.onload = () => {
        const result = reader.result as string
        // Remove data URL prefix (e.g., "data:image/jpeg;base64,")
        const base64 = result.split(",")[1]
        resolve(base64)
      }
      reader.onerror = () => reject(new Error("Failed to read file"))
      reader.readAsDataURL(file)
    })
  }, [])

  // =============================================================================
  // CALLBACKS (stable - memoized)
  // =============================================================================

  const subscribeToEvent = useCallback(
    <T = unknown,>(
      eventType: string,
      listener: EventListener<T>
    ): (() => void) => {
      if (!eventListenersRef.current.has(eventType)) {
        eventListenersRef.current.set(eventType, new Set())
      }
      eventListenersRef.current.get(eventType)!.add(listener as EventListener)

      return () => {
        const listeners = eventListenersRef.current.get(eventType)
        if (listeners) {
          listeners.delete(listener as EventListener)
          if (listeners.size === 0) {
            eventListenersRef.current.delete(eventType)
          }
        }
      }
    },
    []
  )

  const dispatchEvent = useCallback((eventType: string, data: unknown) => {
    const listeners = eventListenersRef.current.get(eventType)
    if (listeners) {
      listeners.forEach((listener) => {
        try {
          listener(data)
        } catch (err) {
          console.error(`Error in event listener for "${eventType}":`, err)
        }
      })
    }
  }, [])

  const addFiles = useCallback(
    (filesToAdd: File[] | FileList) => {
      // Queue files for immediate UI display
      const items = uploadManager.queueFiles(filesToAdd)

      // Add pending files to state
      setPendingFiles((prev) => [...prev, ...items.map((i) => i.pending)])

      // Start uploads in parallel
      for (const { pending, file } of items) {
        uploadManager
          .executeUpload(
            threadId,
            file,
            pending.id,
            clientRef.current,
            (updates) => {
              setPendingFiles((prev) =>
                prev.map((f) =>
                  f.id === pending.id ? { ...f, ...updates } : f
                )
              )
            }
          )
          .catch(() => {
            // Error already handled via onUpdate callback
          })
      }
    },
    [threadId]
  )

  const removeFile = useCallback((id: string) => {
    setPendingFiles((prev) => prev.filter((f) => f.id !== id))
  }, [])

  const getFileUrl = useCallback(
    (file: ThreadFile): string => {
      if (!file.path) return ""
      return clientRef.current.getFileUrl(threadId, file.path)
    },
    [threadId]
  )

  const getThumbnailUrl = useCallback(
    (file: ThreadFile): string => {
      if (!file.path) return ""
      return clientRef.current.getThumbnailUrl(threadId, file.path)
    },
    [threadId]
  )

  const getPreviewUrl = useCallback(
    (file: ThreadFile): string | null => {
      if (!file.isImage) return null
      if (file.localPreviewUrl) return file.localPreviewUrl
      if (file.path) return clientRef.current.getThumbnailUrl(threadId, file.path)
      return null
    },
    [threadId]
  )

  // =============================================================================
  // FILE LOADING AND EVENT HANDLERS
  // =============================================================================

  const loadFiles = useCallback(async () => {
    try {
      const fileList = await clientRef.current.listFiles(threadId)
      setServerFiles(
        fileList
          .filter((f) => !f.isDirectory)
          .map((f) => ({
            id: f.path,
            name: f.name,
            path: f.path,
            mimeType: f.mimeType,
            size: f.size,
            isImage: f.mimeType.startsWith("image/"),
            status: "committed" as const,
            localPreviewUrl: null,
          }))
      )
    } catch (err) {
      console.error("Failed to load files:", err)
    }
  }, [threadId])

  const handleFileCreated = useCallback(
    (data: { path: string; file: { name: string; mimeType: string; size: number; isDirectory?: boolean } }) => {
      if (data.file.isDirectory) return
      const newFile: ThreadFile = {
        id: data.path,
        name: data.file.name,
        path: data.path,
        mimeType: data.file.mimeType,
        size: data.file.size,
        isImage: data.file.mimeType?.startsWith("image/") || false,
        status: "committed" as const,
        localPreviewUrl: null,
      }
      setServerFiles((prev) => {
        const existingIndex = prev.findIndex((f) => f.path === data.path)
        if (existingIndex >= 0) {
          const updated = [...prev]
          updated[existingIndex] = newFile
          return updated
        }
        return [...prev, newFile]
      })
    },
    []
  )

  const handleFileUpdated = useCallback(
    (data: { path: string; file: { name: string; mimeType: string; size: number; isDirectory?: boolean } }) => {
      if (data.file.isDirectory) return
      setServerFiles((prev) =>
        prev.map((f) =>
          f.path === data.path
            ? {
                ...f,
                name: data.file.name,
                mimeType: data.file.mimeType,
                size: data.file.size,
                isImage: data.file.mimeType?.startsWith("image/") || false,
              }
            : f
        )
      )
    },
    []
  )

  const handleFileDeleted = useCallback((data: { path: string }) => {
    setServerFiles((prev) => prev.filter((f) => f.path !== data.path))
  }, [])

  // Attachment management (for sending with messages, NOT uploaded to filesystem)
  const addAttachment = useCallback((input: File | File[] | FileList) => {
    const files =
      input instanceof FileList
        ? Array.from(input)
        : Array.isArray(input)
          ? input
          : [input]

    const newAttachments: PendingAttachment[] = files.map((file) => {
      const isImage = file.type.startsWith("image/")
      return {
        id: createClientId(),
        file,
        name: file.name,
        mimeType: file.type,
        size: file.size,
        isImage,
        previewUrl: isImage ? URL.createObjectURL(file) : null,
      }
    })

    setAttachments((prev) => [...prev, ...newAttachments])
  }, [])

  const removeAttachment = useCallback((id: string) => {
    setAttachments((prev) => {
      const attachment = prev.find((a) => a.id === id)
      if (attachment?.previewUrl) {
        URL.revokeObjectURL(attachment.previewUrl)
      }
      return prev.filter((a) => a.id !== id)
    })
  }, [])

  const clearAttachments = useCallback(() => {
    setAttachments((prev) => {
      // Revoke all preview URLs
      prev.forEach((a) => {
        if (a.previewUrl) URL.revokeObjectURL(a.previewUrl)
      })
      return []
    })
  }, [])

  const sendMessage = useCallback(
    async (payload: Omit<SendMessagePayload, "attachments">): Promise<Message> => {
      // Build optimistic message with attachment previews
      const optimisticId = `optimistic-${createClientId()}`
      const optimisticAttachments = attachments.map((a) => ({
        id: a.id,
        type: "file" as const,
        path: "", // No path yet - will be assigned by server
        name: a.name,
        mimeType: a.mimeType,
        size: a.size,
        width: a.width,
        height: a.height,
        localPreviewUrl: a.previewUrl || undefined,
      }))

      const optimisticMessage: Message = {
        id: optimisticId,
        role: payload.role,
        content: payload.content,
        attachments:
          optimisticAttachments.length > 0
            ? JSON.stringify(optimisticAttachments)
            : null,
        created_at: Date.now() * 1000, // microseconds
        status: "pending",
      }

      // Add optimistic message immediately
      setMessages((prev) => [...prev, optimisticMessage])

      // Capture current attachments before clearing
      const currentAttachments = [...attachments]

      // Clear attachments immediately (optimistic)
      setAttachments([])

      try {
        // Convert attachments to base64 for API
        const attachmentPayloads: AttachmentPayload[] = await Promise.all(
          currentAttachments.map(async (a) => ({
            name: a.name,
            mimeType: a.mimeType,
            data: await fileToBase64(a.file),
            width: a.width,
            height: a.height,
          }))
        )

        const result = await clientRef.current.sendMessage(threadId, {
          ...payload,
          attachments: attachmentPayloads.length > 0 ? attachmentPayloads : undefined,
        })

        // Remove optimistic message - real one will come via WebSocket
        setMessages((prev) => prev.filter((m) => m.id !== optimisticId))

        // Revoke object URLs now that we're done
        currentAttachments.forEach((a) => {
          if (a.previewUrl) URL.revokeObjectURL(a.previewUrl)
        })

        return result
      } catch (err) {
        // Restore attachments on error
        setAttachments(currentAttachments)
        // Remove optimistic message on error
        setMessages((prev) => prev.filter((m) => m.id !== optimisticId))
        setError(err instanceof Error ? err : new Error(String(err)))
        throw err
      }
    },
    [threadId, attachments, fileToBase64]
  )

  const stopExecution = useCallback(async () => {
    try {
      await clientRef.current.stopExecution(threadId)
    } catch (err) {
      setError(err instanceof Error ? err : new Error(String(err)))
      throw err
    }
  }, [threadId])

  const deleteMessage = useCallback(
    async (messageId: string) => {
      // Optimistic update - remove from UI immediately
      const previousMessages = messagesRef.current
      setMessages((prev) => prev.filter((m) => m.id !== messageId))

      try {
        await clientRef.current.deleteMessage(threadId, messageId)
      } catch (err) {
        // Rollback on error
        setMessages(previousMessages)
        setError(err instanceof Error ? err : new Error(String(err)))
        throw err
      }
    },
    [threadId]
  )

  // =============================================================================
  // WEBSOCKET CONNECTION (via ThreadConnectionManager)
  // =============================================================================

  useEffect(() => {
    if (!live || !threadId) return

    console.log(`[ThreadProvider] Connecting WebSocket for thread ${threadId}`)

    const manager = new ThreadConnectionManager(
      clientRef.current,
      threadId,
      {
        onStatusChange: (status) => {
          console.log(`[ThreadProvider] Connection status: ${status}`)
          setConnectionStatus(status)
        },
        onMessage: (event) => {
          setMessages((prev) => {
            const exists = prev.some((m) => m.id === event.data.id)
            if (exists) {
              return prev.map((m) => (m.id === event.data.id ? event.data : m))
            } else {
              return [...prev, event.data]
            }
          })
        },
        onChunk: (event) => {
          setMessages((prev) => {
            return prev.map((m) => {
              if (m.id === event.message_id) {
                return {
                  ...m,
                  content: (m.content || "") + event.chunk,
                }
              }
              return m
            })
          })
        },
        onEvent: (event) => {
          // Handle file events
          if (event.eventType === "file_created") {
            handleFileCreated(event.data as { path: string; file: { name: string; mimeType: string; size: number; isDirectory?: boolean } })
          } else if (event.eventType === "file_updated") {
            handleFileUpdated(event.data as { path: string; file: { name: string; mimeType: string; size: number; isDirectory?: boolean } })
          } else if (event.eventType === "file_deleted") {
            handleFileDeleted(event.data as { path: string })
          }
          // Dispatch to registered custom event handlers
          dispatchEvent(event.eventType, event.data)
        },
        onError: (event) => {
          console.error("[ThreadProvider] WebSocket error:", event.error)
          setError(new Error(event.error))
        },
      },
      {
        depth,
        includeSilent,
      }
    )

    connectionManagerRef.current = manager
    manager.connect()

    return () => {
      manager.disconnect()
      connectionManagerRef.current = null
    }
  }, [threadId, live, depth, includeSilent, dispatchEvent, handleFileCreated, handleFileUpdated, handleFileDeleted])

  // Fetch initial messages
  useEffect(() => {
    if (!preload || !threadId) return

    const fetchMessages = async () => {
      setLoading(true)
      setError(null)

      try {
        const fetchedMessages = await clientRef.current.getMessages(threadId, {
          depth,
          includeSilent,
        })
        setMessages(fetchedMessages)
      } catch (err) {
        console.error("Failed to fetch messages:", err)
        setError(
          err instanceof Error ? err : new Error("Failed to fetch messages")
        )
        setMessages([])
      } finally {
        setLoading(false)
      }
    }

    fetchMessages()
  }, [threadId, preload, depth, includeSilent])

  // Load files on mount and when threadId changes
  useEffect(() => {
    if (!threadId) return

    // Reset serverFiles when threadId changes
    setServerFiles([])
    loadFiles()
  }, [threadId, loadFiles])

  // =============================================================================
  // MEMOIZED CONTEXT VALUES (split for performance)
  // =============================================================================

  const staticValue = useMemo(
    (): StaticContextValue => ({
      threadId,
      options: {
        preload,
        live,
        useWorkblocks,
        useSubagentBlocks,
        depth,
        includeSilent,
      },
      sendMessage,
      stopExecution,
      deleteMessage,
      subscribeToEvent,
      addFiles,
      removeFile,
      getFileUrl,
      getThumbnailUrl,
      getPreviewUrl,
      addAttachment,
      removeAttachment,
      clearAttachments,
    }),
    [
      threadId,
      preload,
      live,
      useWorkblocks,
      useSubagentBlocks,
      depth,
      includeSilent,
      sendMessage,
      stopExecution,
      deleteMessage,
      subscribeToEvent,
      addFiles,
      removeFile,
      getFileUrl,
      getThumbnailUrl,
      getPreviewUrl,
      addAttachment,
      removeAttachment,
      clearAttachments,
    ]
  )

  const messagesValue = useMemo(
    (): MessagesContextValue => ({
      messages,
      workblocks,
      subagentBlocks,
      groupedMessages,
      loading,
    }),
    [messages, workblocks, subagentBlocks, groupedMessages, loading]
  )

  const filesValue = useMemo(
    (): FilesContextValue => ({
      files,
    }),
    [files]
  )

  const connectionValue = useMemo(
    (): ConnectionContextValue => ({
      connectionStatus,
      error,
    }),
    [connectionStatus, error]
  )

  const attachmentsValue = useMemo(
    (): AttachmentsContextValue => ({
      attachments,
    }),
    [attachments]
  )

  // =============================================================================
  // RENDER
  // =============================================================================

  return (
    <StaticContext.Provider value={staticValue}>
      <MessagesContext.Provider value={messagesValue}>
        <FilesContext.Provider value={filesValue}>
          <AttachmentsContext.Provider value={attachmentsValue}>
            <ConnectionContext.Provider value={connectionValue}>
              {children}
            </ConnectionContext.Provider>
          </AttachmentsContext.Provider>
        </FilesContext.Provider>
      </MessagesContext.Provider>
    </StaticContext.Provider>
  )
}

// =============================================================================
// INTERNAL HOOKS (used by useThread)
// =============================================================================

function useStaticContext(): StaticContextValue {
  const context = useContext(StaticContext)
  if (!context) {
    throw new Error("useThread must be used within a ThreadProvider")
  }
  return context
}

function useMessagesContext(): MessagesContextValue {
  const context = useContext(MessagesContext)
  if (!context) {
    throw new Error("useThread must be used within a ThreadProvider")
  }
  return context
}

function useFilesContext(): FilesContextValue {
  const context = useContext(FilesContext)
  if (!context) {
    throw new Error("useThread must be used within a ThreadProvider")
  }
  return context
}

function useConnectionContext(): ConnectionContextValue {
  const context = useContext(ConnectionContext)
  if (!context) {
    throw new Error("useThread must be used within a ThreadProvider")
  }
  return context
}

function useAttachmentsContext(): AttachmentsContextValue {
  const context = useContext(AttachmentsContext)
  if (!context) {
    throw new Error("useThread must be used within a ThreadProvider")
  }
  return context
}

// =============================================================================
// PUBLIC HOOKS
// =============================================================================

/**
 * Hook to access thread context with optimized re-renders.
 * Components only re-render when the specific data they use changes.
 *
 * @internal Used by useThread() in hooks/useThread.ts
 */
export function useThreadContextInternal(): ThreadContextValue {
  const static_ = useStaticContext()
  const messages_ = useMessagesContext()
  const files_ = useFilesContext()
  const attachments_ = useAttachmentsContext()
  const connection_ = useConnectionContext()

  // Note: This object is recreated on every render of a consumer,
  // but the individual context subscriptions are optimized.
  // A component using only `sendMessage` won't re-render when messages change.
  return {
    threadId: static_.threadId,
    options: static_.options,
    sendMessage: static_.sendMessage,
    stopExecution: static_.stopExecution,
    deleteMessage: static_.deleteMessage,
    subscribeToEvent: static_.subscribeToEvent,
    onEvent: static_.subscribeToEvent,
    // File management (uploads to filesystem)
    addFiles: static_.addFiles,
    removeFile: static_.removeFile,
    getFileUrl: static_.getFileUrl,
    getThumbnailUrl: static_.getThumbnailUrl,
    getPreviewUrl: static_.getPreviewUrl,
    // Attachment management (sent inline with messages)
    addAttachment: static_.addAttachment,
    removeAttachment: static_.removeAttachment,
    clearAttachments: static_.clearAttachments,
    attachments: attachments_.attachments,
    // Messages
    messages: messages_.messages,
    workblocks: messages_.workblocks,
    subagentBlocks: messages_.subagentBlocks,
    groupedMessages: messages_.groupedMessages,
    loading: messages_.loading,
    isLoading: messages_.loading,
    files: files_.files,
    connectionStatus: connection_.connectionStatus,
    status: connection_.connectionStatus,
    error: connection_.error,
  }
}

// Track if we've already warned to avoid spam
let hasWarnedAboutUseThreadContext = false

/**
 * @deprecated Use `useThread()` instead.
 *
 * Hook to access the thread context.
 * Must be used within a ThreadProvider.
 *
 * @throws Error if used outside of ThreadProvider
 */
export function useThreadContext(): ThreadContextValue {
  if (
    !hasWarnedAboutUseThreadContext &&
    process.env.NODE_ENV !== "production"
  ) {
    hasWarnedAboutUseThreadContext = true
    console.warn(
      "[DEPRECATED] useThreadContext() is deprecated.\n" +
        "Use: const { messages, sendMessage, ... } = useThread()"
    )
  }

  return useThreadContextInternal()
}

/**
 * Hook to get the current thread ID from context.
 * Must be used within a ThreadProvider.
 */
export function useThreadId(): string {
  return useStaticContext().threadId
}
